import { ErrorHandler, Injectable, inject } from '@angular/core';
import { MessageService } from 'primeng/api';

import { LoggingService } from './logging.service';

/**
 * Thin wrapper around `location.reload()` so `GlobalErrorHandler` can be
 * unit-tested without triggering a real page reload -- specs inject/spy on
 * this instead of the global `location` object.
 */
@Injectable({ providedIn: 'root' })
export class PageReloader {
  reload(): void {
    window.location.reload();
  }
}

/**
 * Matches the "stale chunk after a deploy" family of errors: the browser
 * still has an old `index.html` referencing a hashed chunk filename that no
 * longer exists on the server. Covers the Chromium/Firefox wordings plus the
 * generic `ChunkLoadError` some bundlers use.
 */
const CHUNK_LOAD_ERROR_PATTERN =
  /failed to fetch dynamically imported module|error loading dynamically imported module|chunkloaderror/i;

/** sessionStorage key marking "already reloaded once for a chunk error". */
const RELOAD_MARK_KEY = 'sh_chunk_reload_attempted';

/**
 * Grace window after construction with no repeat error: treated as a
 * successful boot, so the reload guard is cleared and the next stale-deploy
 * hit gets its own automatic reload again instead of only the warning.
 */
export const SUCCESSFUL_BOOT_MS = 10_000;

/**
 * Routes every uncaught client error to the LoggingService (which forwards it
 * to the backend ingest endpoint and keeps dev console visibility).
 *
 * Additionally detects stale dynamically-imported-chunk failures (the app was
 * left open across a deploy) and recovers automatically: reloads the page
 * once, guarding against a reload loop via sessionStorage, and warns the user
 * instead of reloading again if the error persists in the same session.
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private readonly logging = inject(LoggingService);
  private readonly messageService = inject(MessageService);
  private readonly pageReloader = inject(PageReloader);

  /**
   * Set as soon as a chunk-load error is handled during this page load. Guards
   * the boot-timer cleanup below: without it, the timer would clear the guard
   * mark unconditionally even when this very load already hit a chunk error,
   * turning "one reload per session" into "one reload every
   * SUCCESSFUL_BOOT_MS" -- the reload loop this guard exists to prevent.
   */
  private chunkErrorSeen = false;

  constructor() {
    // If nothing else goes wrong for a while after this handler is built, this
    // load counts as a successful boot: clear any guard left by a previous
    // reload so a later deploy can also trigger one automatic recovery. But
    // skip the cleanup if a chunk error already happened during this same
    // boot -- that error was already handled (reloaded or warned) and the
    // mark must survive so a repeat doesn't reload again.
    setTimeout(() => {
      if (!this.chunkErrorSeen) {
        this.clearReloadMark();
      }
    }, SUCCESSFUL_BOOT_MS);
  }

  handleError(error: unknown): void {
    this.logging.captureError(error);

    if (!this.isChunkLoadError(error)) {
      return;
    }

    this.chunkErrorSeen = true;

    if (this.hasReloadedAlready()) {
      this.warnUser();
      return;
    }

    if (!this.markReloaded()) {
      // Couldn't persist the guard (private mode, storage blocked by policy,
      // etc.): reloading here would be indistinguishable from a fresh load on
      // the next hit, so it would loop forever instead of "once per session".
      // Only warn -- no guard, no automatic reload.
      this.warnUser();
      return;
    }

    this.pageReloader.reload();
  }

  private isChunkLoadError(error: unknown): boolean {
    const text =
      error instanceof Error
        ? `${error.name} ${error.message}`
        : typeof error === 'string'
          ? error
          : '';
    return CHUNK_LOAD_ERROR_PATTERN.test(text);
  }

  private hasReloadedAlready(): boolean {
    try {
      return sessionStorage.getItem(RELOAD_MARK_KEY) === '1';
    } catch {
      return false;
    }
  }

  /** Returns whether the guard mark was actually persisted. */
  private markReloaded(): boolean {
    try {
      sessionStorage.setItem(RELOAD_MARK_KEY, '1');
      return true;
    } catch {
      return false;
    }
  }

  private clearReloadMark(): void {
    try {
      sessionStorage.removeItem(RELOAD_MARK_KEY);
    } catch {
      // Nothing to clear without storage access.
    }
  }

  private warnUser(): void {
    this.messageService.add({
      severity: 'warn',
      summary: 'Nueva versión disponible',
      detail: 'Hay una versión nueva de la aplicación, recargue la página.',
      sticky: true,
    });
  }
}
